import React from "react";
import { HiArrowLeft, HiDotsVertical } from "react-icons/hi";
import { FaCircleUser, FaUserGroup } from "react-icons/fa6";
import StatusPill from "./StatusPill";
import { Dropdown } from "flowbite-react";
import type { ChatRoom } from "@/types/chat.types"; 

interface Props {
  currentRoom: ChatRoom | null;
  isSmallScreen: boolean;
  onGoBack: () => void;
  onOpenDrawer: () => void;
  onInvite: () => void;
  onCloseSession: () => void;
  onLeave: () => void;
  onDestroy: () => void;
}

const ChatHeader: React.FC<Props> = ({
  currentRoom,
  isSmallScreen,
  onGoBack,
  onOpenDrawer,
  onInvite,
  onCloseSession,
  onLeave,
  onDestroy,
}) => {
  // Helper functions to check session type
  const isGroup = currentRoom?.session_type === "group-chat";
  const isConference = currentRoom?.session_type === "conference";
  const isAgentToAgent = currentRoom?.session_type === "agent-to-agent";
  const isCreator = currentRoom?.isCreator;
  const isClientToAgent = currentRoom?.session_type === "c2a";

  const hasAvailableActions = () => {
    const canClose = !!(
      currentRoom?.isSessionActive &&
      !isGroup &&
      !isConference &&
      !isAgentToAgent
    );
    const canInvite = !!(
      !isAgentToAgent &&
      currentRoom?.isSessionActive &&
      (!isGroup && !isConference)
    );
    const canInviteCreator = !!(
      !isAgentToAgent &&
      currentRoom?.isSessionActive &&
      (isGroup || isConference) &&
      isCreator
    );
    const canLeave = !!(
      (isConference || isGroup) &&
      currentRoom?.isSessionActive
    );
    const canDestroy = !!(
      (isConference || isGroup) &&
      isCreator &&
      currentRoom?.isSessionActive
    );
    return canClose || canInvite || canInviteCreator || canLeave || canDestroy;
  };

  // Get display name based on session type
  const getDisplayName = () => {
    if (!currentRoom) return "";

    if (isGroup) {
      // For group chats, remove prefix
      return currentRoom.session_key.startsWith("grpcht:")
        ? currentRoom.session_key.substring(7)
        : currentRoom.session_key;
    }

    if (isConference) {
      // For conferences, remove prefix
      return currentRoom.session_key.startsWith("conf:")
        ? currentRoom.session_key.substring(5)
        : currentRoom.session_key;
    }

    if (isClientToAgent) {
      // For client-to-agent, show client username
      return currentRoom.client?.client_username || currentRoom.session_key;
    }

    if (isAgentToAgent) {
      // For agent-to-agent, find the receiver from participants where is_current_user is false
      if (currentRoom.participants && currentRoom.participants.length > 0) {
        const receiver = currentRoom.participants.find(p => p.is_current_user === false);
        if (receiver?.details && receiver.user_type === 3) {
          return (receiver.details as any).agent_username || currentRoom.session_key;
        }
      }
      // Fallback: try to extract from session_key
      const parts = currentRoom.session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
      return parts[0] || currentRoom.session_key;
    }

    // Fallback
    return currentRoom.session_key;
  };

  const title = getDisplayName();
  return (
    <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800 sticky top-0 z-10">
      <div className="flex items-center">
        <div className="pr-3">
          {isSmallScreen && (
            <a
              onClick={onGoBack}
              className="inline-flex justify-center p-1 text-gray-500 rounded cursor-pointer hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
            >
              <span className="sr-only">Go back</span>
              <HiArrowLeft className="text-2xl" />
            </a>
          )}
        </div>
        <div className="shrink-0">
          <button onClick={onOpenDrawer}>
            {isConference || isGroup ? (
              <FaUserGroup className="text-gray-600 h-6 w-6" />
            ) : (
              <FaCircleUser className="text-gray-600 h-6 w-6" />
            )}
          </button>
        </div>
        <div className="mb-1 ml-4">
          <div className="text-base font-semibold text-gray-900 truncate dark:text-white hover:border hover:border-gray-200 hover:dark:border-gray-700 hover:rounded-lg hover:bg-gray-200 hover:dark:bg-gray-500 hover:rounded px-3 py-1">
            <button onClick={onOpenDrawer}>{title}</button>
          </div>
        </div>
        <StatusPill
          active={!!currentRoom?.isSessionActive}
          className="hidden sm:inline-flex ml-3"
        />
      </div>

      <div className="flex items-center space-x-2">
        <StatusPill
          active={!!currentRoom?.isSessionActive}
          className="sm:hidden inline-flex"
        />
        <div className="hidden pl-0 space-x-2 sm:flex sm:px-2">
          <div className="pr-2">
            <Dropdown
              color="gray"
              inline
              label={
                <HiDotsVertical className="text-gray-700 dark:text-gray-400 hover:text-gray-700 rounded-full w-5 h-5" />
              }
              theme={{ arrowIcon: "hidden" }}
              className="p-1"
            >
              {currentRoom?.isSessionActive &&
                !isGroup &&
                !isConference &&
                !isAgentToAgent && (
                  <Dropdown.Item onClick={onCloseSession}>
                    Close the chat
                  </Dropdown.Item>
                )}
              {currentRoom?.isSessionActive &&
                !isAgentToAgent &&
                !isGroup &&
                !isConference && (
                  <Dropdown.Item onClick={onInvite}>
                    Invite a member
                  </Dropdown.Item>
                )}
              {currentRoom?.isSessionActive &&
                !isAgentToAgent &&
                (isGroup || isConference) &&
                isCreator && (
                  <Dropdown.Item onClick={onInvite}>
                    Invite a member
                  </Dropdown.Item>
                )}
              {(isConference || isGroup) &&
                currentRoom?.isSessionActive && (
                  <Dropdown.Item onClick={onLeave}>Leave chat</Dropdown.Item>
                )}
              {(isConference || isGroup) &&
                isCreator &&
                currentRoom?.isSessionActive && (
                  <Dropdown.Item onClick={onDestroy}>
                    Close the chat
                  </Dropdown.Item>
                )}
              {!hasAvailableActions() && (
                <Dropdown.Item
                  className="text-gray-400 cursor-not-allowed"
                  onClick={() => {}}
                >
                  No action available
                </Dropdown.Item>
              )}
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ChatHeader;
